import { CAMPAIGN_INFO } from "@/constants/campaign";
import { HANZ_FALLING_CAMPAIGN_ID } from "@/constants/campaign";
import { useIsMobile } from "@/hooks/use-mobile";
import { NewTrackingEvent } from "@/lib/track/track-event";
import { trackNewEvent } from "@/lib/track/track-event";
import { useToastMessage } from "@backpac/monica";
import { Icon, Thumbnail, ThumbnailFallback, ThumbnailImage, Typography, useModal } from "@backpac/monica";
import { type HTMLAttributes, useEffect, useRef, useState } from "react";
import { renderSVG as renderQRAsSVG } from "uqr";

interface MusicTrackProps {
  title: string;
  isrc: string;
  coverImage: string;
}

export function MusicTrack({ title, isrc, coverImage }: MusicTrackProps) {
  const { audio, status } = useAudioPlayer();

  return (
    <div className="flex items-center gap-4 justify-between bg-white p-3 rounded-[6px] w-full">
      <div className="relative">
        <Thumbnail variant="square" className="size-12">
          <ThumbnailImage src={coverImage} />
          <ThumbnailFallback>{title.slice(0, 1).toUpperCase()}</ThumbnailFallback>
        </Thumbnail>
        <div className="bg-light-gray opacity-80 absolute top-0 left-0 w-full h-full inset-0" />

        {status === "playing" ? (
          <button
            type="button"
            className="absolute top-0 left-0 w-full h-full flex items-center justify-center text-white"
            onClick={() => {
              audio?.pause();
            }}
          >
            <i className="i-bp-pause-fill inline-block w-4 h-4" />
          </button>
        ) : (
          <button
            type="button"
            className="absolute top-0 left-0 w-full h-full flex items-center justify-center"
            onClick={() => {
              trackNewEvent(NewTrackingEvent.ClickCampaignMusicTrack, {
                isrc,
                campaign_id: HANZ_FALLING_CAMPAIGN_ID,
              });
              audio?.play();
            }}
          >
            <i className="i-bp-play-fill inline-block w-4 h-4" />
          </button>
        )}
      </div>
      <div className="flex flex-col items-start justify-center gap-1">
        <Typography size="xs">{title}</Typography>
        <Typography className="whitespace-nowrap" size="xs" color="textMuted">
          {isrc}
        </Typography>
      </div>
      <div className="flex gap-2 ml-auto">
        <ISRCCopyButton isrc={isrc} />
        <QRCodeButton isrc={isrc} title={title} youtubeVideoId={isrc} thumbnailImageUrl={coverImage} />
      </div>
    </div>
  );
}

interface ISRCCopyButtonProps extends HTMLAttributes<HTMLButtonElement> {
  isrc: string;
}

function ISRCCopyButton({ isrc, onClick, ...props }: ISRCCopyButtonProps) {
  const { info, error } = useToastMessage();

  return (
    <button
      className="flex items-center justify-center border border-[#00000033] rounded-full p-4"
      onClick={(e) => {
        e.stopPropagation();
        onClick?.(e);
        navigator.clipboard
          .writeText(isrc)
          .then(() => {
            info(`Code copied '${isrc}'`);
          })
          .catch(() => {
            error(`Failed to copy: ${isrc}`);
          });
      }}
      {...props}
    >
      <Icon name="copy" className="w-4 h-4" />
    </button>
  );
}

interface QRCodeButtonProps extends HTMLAttributes<HTMLButtonElement> {
  thumbnailImageUrl: string;
  isrc: string;
  title: string;
  youtubeVideoId: string;
}

function QRCodeButton({ thumbnailImageUrl, isrc, title, youtubeVideoId, onClick, ...props }: QRCodeButtonProps) {
  const isMobile = useIsMobile();
  const { open: openQRCodeModal } = useQRCodeModal({
    thumbnailImageUrl,
    isrc,
    title,
    url: CAMPAIGN_INFO[HANZ_FALLING_CAMPAIGN_ID].material.shortsUrl,
  });

  return (
    <button
      className="flex items-center justify-center bg-black text-white rounded-full p-4"
      onClick={(e) => {
        if (isMobile) {
          location.href = CAMPAIGN_INFO[HANZ_FALLING_CAMPAIGN_ID].material.shortsUrl;
          onClick?.(e);
        } else {
          e.stopPropagation();
          openQRCodeModal();
          onClick?.(e);
        }
      }}
      {...props}
    >
      <Icon name="qr-code" className="w-4 h-4" />
    </button>
  );
}

function useQRCodeModal({
  url,
  thumbnailImageUrl,
  title,
  isrc,
}: { url: string; thumbnailImageUrl: string; title: string; isrc: string }) {
  const { open: modalOpen } = useModal();

  const open = () =>
    modalOpen({
      title: "Scan QR Code",
      className: "max-w-[300px]",
      withNoHeader: true,
      children: (
        <div className="flex flex-col items-center gap-4 pt-10">
          <Typography font="unbounded" color="textPrimary" size="lg" weight={400}>
            Scan QR Code
          </Typography>
          <div
            className="size-[200px] p-[40px]"
            style={{
              backgroundImage: "url(/assets/qr-background.svg)",
            }}
            dangerouslySetInnerHTML={{ __html: renderQRAsSVG(url) }}
          />
          <div className="flex items-center gap-3 w-full">
            <Thumbnail variant="square" className="size-10">
              <ThumbnailImage src={thumbnailImageUrl} alt={title} />
              <ThumbnailFallback>{title.slice(0, 2)}</ThumbnailFallback>
            </Thumbnail>
            <div className="flex flex-col gap-1">
              <Typography size="sm" color="textPrimary">
                {title}
              </Typography>
              <Typography size="xs" color="textSecondary">
                {isrc}
              </Typography>
            </div>
          </div>
        </div>
      ),
    });

  return { open };
}

function useAudioPlayer() {
  const [status, setStatus] = useState<"loading" | "ready" | "playing" | "paused">("loading");
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    if (!audioRef.current || audioRef.current.src !== CAMPAIGN_INFO[HANZ_FALLING_CAMPAIGN_ID].material.music) {
      if (audioRef.current) {
        audioRef.current.pause();
      }
      audioRef.current = new Audio(CAMPAIGN_INFO[HANZ_FALLING_CAMPAIGN_ID].material.music);
      audioRef.current.load();

      const handleLoad = () => {
        setStatus("ready");
      };

      const handlePlay = () => {
        setStatus("playing");
      };

      const handlePause = () => {
        setStatus("paused");
      };

      audioRef.current.addEventListener("load", handleLoad);
      audioRef.current.addEventListener("play", handlePlay);
      audioRef.current.addEventListener("pause", handlePause);

      return () => {
        if (audioRef.current) {
          audioRef.current.pause();
          audioRef.current.removeEventListener("load", handleLoad);
          audioRef.current.removeEventListener("play", handlePlay);
          audioRef.current.removeEventListener("pause", handlePause);
        }
      };
    }
  }, []);

  return { audio: audioRef.current, status };
}
